/**
 * Moddalar xossalari — ommaviy API.
 *
 * Barcha funksiyalar sof: (modda, T[, P]) → SI qiymat.
 * Ixtiyoriy `warnings` massivi orqali extrapolyatsiya haqida xabar beradi.
 * Xossa modda uchun mavjud bo'lmasa (masalan havo uchun Antoine) —
 * PropertyUnavailableError tashlanadi: bu dasturchi xatosi, talaba xatosi emas.
 */
import type { Phase, WarningSink } from '../types';
import {
  R_GAS,
  checkRange,
  evalAntoine,
  evalPoly,
  evalShomate,
  evalSutherland,
  evalVogel,
  invertAntoine,
} from './evaluate';
import { integratePoly, integrateShomate } from './enthalpy';
import type { Substance, SubstanceId } from './types';

/**
 * Moddalar reyestri. Engine ichida birorta modda yo'q — ular domain pack
 * (packs/<id>/components.json) dan `registerSubstances` orqali yuklanadi.
 * Shu sababli modul darajasida getSubstance chaqirmaslik kerak (lazy).
 */
const REGISTRY: Record<string, Substance> = {};

export function registerSubstances(list: readonly Substance[]): void {
  for (const s of list) {
    if (!s.id || !(s.molarMass > 0)) throw new Error(`Invalid substance: ${JSON.stringify(s.id)}`);
    REGISTRY[s.id] = s;
  }
}

/** Testlar uchun: reyestrni tozalash */
export function clearSubstances(): void {
  for (const k of Object.keys(REGISTRY)) delete REGISTRY[k];
}

export function substanceIds(): SubstanceId[] {
  return Object.keys(REGISTRY);
}

export function isSubstanceId(id: string): id is SubstanceId {
  return id in REGISTRY;
}

export function getSubstance(id: string): Substance {
  const s = REGISTRY[id];
  if (!s) throw new Error(`Unknown substance: ${id} (pack yuklanganmi?)`);
  return s;
}

export class PropertyUnavailableError extends Error {
  constructor(
    public readonly substance: string,
    public readonly property: string,
  ) {
    super(`Property "${property}" is not available for substance "${substance}"`);
    this.name = 'PropertyUnavailableError';
  }
}

/** Modda: obyekt yoki identifikator ('water', 'ethanol', ...) */
type Sub = Substance | string;
const sub = (s: Sub): Substance => (typeof s === 'string' ? getSubstance(s) : s);

// ─── Faza muvozanati ────────────────────────────────────────────────────────

/** To'yingan bug' bosimi, Pa */
export function saturationPressure(s: Sub, T: number, warnings?: WarningSink): number {
  const m = sub(s);
  if (!m.antoine) throw new PropertyUnavailableError(m.id, 'antoine');
  checkRange(m.antoine.range, T, { substance: m.id, property: 'antoine' }, warnings);
  return evalAntoine(m.antoine, T);
}

/** To'yinish (qaynash) harorati berilgan bosimda, K */
export function saturationTemperature(s: Sub, P: number, warnings?: WarningSink): number {
  const m = sub(s);
  if (!m.antoine) throw new PropertyUnavailableError(m.id, 'antoine');
  const T = invertAntoine(m.antoine, P);
  checkRange(m.antoine.range, T, { substance: m.id, property: 'antoine' }, warnings);
  return T;
}

// ─── Issiqlik sig'imi ───────────────────────────────────────────────────────

/** Cp, J/(kg·K) */
export function heatCapacity(s: Sub, phase: Phase, T: number, warnings?: WarningSink): number {
  const m = sub(s);
  if (phase === 'liquid') {
    if (!m.cpLiquid) throw new PropertyUnavailableError(m.id, 'cpLiquid');
    checkRange(m.cpLiquid.range, T, { substance: m.id, property: 'cpLiquid' }, warnings);
    return evalPoly(m.cpLiquid, T);
  }
  if (!m.cpGas) throw new PropertyUnavailableError(m.id, 'cpGas');
  checkRange(m.cpGas.range, T, { substance: m.id, property: 'cpGas' }, warnings);
  return m.cpGas.form === 'shomate' ? evalShomate(m.cpGas, T, m.molarMass) : evalPoly(m.cpGas, T);
}

/** Entalpiya o'zgarishi ∫_{T1}^{T2} Cp dT, J/kg (faza o'zgarishisiz), analitik */
export function enthalpyChange(
  s: Sub,
  phase: Phase,
  T1: number,
  T2: number,
  warnings?: WarningSink,
): number {
  const m = sub(s);
  const prop = phase === 'liquid' ? m.cpLiquid : m.cpGas;
  const name = phase === 'liquid' ? 'cpLiquid' : 'cpGas';
  if (!prop) throw new PropertyUnavailableError(m.id, name);
  if (warnings) {
    checkRange(prop.range, T1, { substance: m.id, property: name }, warnings);
    checkRange(prop.range, T2, { substance: m.id, property: name }, warnings);
  }
  return prop.form === 'shomate'
    ? integrateShomate(prop, T1, T2, m.molarMass)
    : integratePoly(prop, T1, T2);
}

/** O'rtacha Cp = ΔH/ΔT; T1 = T2 bo'lsa Cp(T1) */
export function meanHeatCapacity(
  s: Sub,
  phase: Phase,
  T1: number,
  T2: number,
  warnings?: WarningSink,
): number {
  if (Math.abs(T2 - T1) < 1e-9) return heatCapacity(s, phase, T1, warnings);
  return enthalpyChange(s, phase, T1, T2, warnings) / (T2 - T1);
}

// ─── Bug'lanish issiqligi ───────────────────────────────────────────────────

/** r(T), J/kg — Watson korrelyatsiyasi */
export function heatOfVaporization(s: Sub, T: number, warnings?: WarningSink): number {
  const m = sub(s);
  if (!m.hvap || m.Tb === null || m.Tc === null) throw new PropertyUnavailableError(m.id, 'hvap');
  if (T >= m.Tc) {
    checkRange([0, m.Tc], T, { substance: m.id, property: 'hvap' }, warnings);
    return 0;
  }
  const ratio = (m.Tc - T) / (m.Tc - m.Tb);
  return m.hvap.atTb * Math.pow(ratio, m.hvap.n);
}

// ─── Zichlik ────────────────────────────────────────────────────────────────

/** Suyuqlik zichligi, kg/m³ */
export function liquidDensity(s: Sub, T: number, warnings?: WarningSink): number {
  const m = sub(s);
  if (!m.densityLiquid) throw new PropertyUnavailableError(m.id, 'densityLiquid');
  checkRange(m.densityLiquid.range, T, { substance: m.id, property: 'densityLiquid' }, warnings);
  return evalPoly(m.densityLiquid, T);
}

/** Gaz zichligi, ideal gaz: ρ = P·M/(R·T). ASSUMPTION: past bosim (< ~10 bar). */
export function gasDensity(s: Sub, T: number, P: number): number {
  const m = sub(s);
  return (P * m.molarMass) / (R_GAS * T);
}

export function density(
  s: Sub,
  phase: Phase,
  T: number,
  P: number,
  warnings?: WarningSink,
): number {
  return phase === 'liquid' ? liquidDensity(s, T, warnings) : gasDensity(s, T, P);
}

// ─── Yopishqoqlik ───────────────────────────────────────────────────────────

/** Dinamik yopishqoqlik, Pa·s */
export function viscosity(s: Sub, phase: Phase, T: number, warnings?: WarningSink): number {
  const m = sub(s);
  if (phase === 'liquid') {
    if (!m.viscosityLiquid) throw new PropertyUnavailableError(m.id, 'viscosityLiquid');
    checkRange(
      m.viscosityLiquid.range,
      T,
      { substance: m.id, property: 'viscosityLiquid' },
      warnings,
    );
    return evalVogel(m.viscosityLiquid, T);
  }
  if (!m.viscosityGas) throw new PropertyUnavailableError(m.id, 'viscosityGas');
  checkRange(m.viscosityGas.range, T, { substance: m.id, property: 'viscosityGas' }, warnings);
  return evalSutherland(m.viscosityGas, T);
}

// ─── Issiqlik o'tkazuvchanlik ───────────────────────────────────────────────

/** λ, W/(m·K) */
export function thermalConductivity(
  s: Sub,
  phase: Phase,
  T: number,
  warnings?: WarningSink,
): number {
  const m = sub(s);
  const p = phase === 'liquid' ? m.conductivityLiquid : m.conductivityGas;
  const name = phase === 'liquid' ? 'conductivityLiquid' : 'conductivityGas';
  if (!p) throw new PropertyUnavailableError(m.id, name);
  checkRange(p.range, T, { substance: m.id, property: name }, warnings);
  return evalPoly(p, T);
}

/** Prandtl soni Pr = Cp·μ/λ */
export function prandtl(s: Sub, phase: Phase, T: number, warnings?: WarningSink): number {
  const m = sub(s);
  return (
    (heatCapacity(m, phase, T, warnings) * viscosity(m, phase, T, warnings)) /
    thermalConductivity(m, phase, T, warnings)
  );
}

export type { Substance, SubstanceId } from './types';
export * from './types';
export { R_GAS, MMHG_TO_PA, integrate1D } from './evaluate';
